import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { Loader2, AlertTriangle, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { PublicAthleteProfile } from '../../types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ViewState =
  | { kind: 'loading' }
  | { kind: 'loaded'; athlete: PublicAthleteProfile }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

interface PublicAthleteViewProps {
  athleteId: string;
}

async function fetchPublicAthlete(athleteId: string): Promise<
  | { ok: true; athlete: PublicAthleteProfile }
  | { ok: false; code: 'not_found' }
  | { ok: false; code: 'error'; message: string }
> {
  const { data, error } = await supabase.functions.invoke('get-athlete-public-profile', {
    body: { athlete_id: athleteId },
  });

  if (error) return { ok: false, code: 'error', message: error.message };
  if (data?.code === 'not_found') return { ok: false, code: 'not_found' };
  return { ok: true, athlete: data.athlete as PublicAthleteProfile };
}

function AthleteCard({ athlete }: { athlete: PublicAthleteProfile }) {
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
      {/* Header row: avatar + name + jersey */}
      <div className="flex items-start gap-3">
        {athlete.avatar_url ? (
          <img
            src={athlete.avatar_url}
            alt={athlete.full_name}
            className="w-12 h-12 rounded-xl object-cover shrink-0 bg-slate-100"
          />
        ) : (
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <User size={20} className="text-slate-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-black text-slate-900 truncate">{athlete.full_name}</p>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
            {athlete.sport.replace(/_/g, ' ')} · Class of {athlete.grad_year}
          </p>
          {athlete.school && (
            <p className="text-xs font-medium text-slate-500 mt-0.5 truncate">{athlete.school}</p>
          )}
        </div>
        {athlete.jersey_number && (
          <span className="text-xs font-black text-slate-400 shrink-0">
            #{athlete.jersey_number}
          </span>
        )}
      </div>

      {/* Positions */}
      {athlete.positions && athlete.positions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {athlete.positions.map((pos) => (
            <span
              key={pos}
              className="px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 text-[9px] font-black uppercase tracking-widest"
            >
              {pos}
            </span>
          ))}
        </div>
      )}

      {/* Measurables */}
      <div className="mt-3 flex flex-wrap gap-2">
        {athlete.height && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.height}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Ht</p>
          </div>
        )}
        {athlete.weight && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.weight}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Wt</p>
          </div>
        )}
        {athlete.gpa !== undefined && athlete.gpa !== null && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.gpa.toFixed(1)}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">GPA</p>
          </div>
        )}
        {athlete.sat_score && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.sat_score}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">SAT</p>
          </div>
        )}
        {athlete.act_score && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.act_score}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">ACT</p>
          </div>
        )}
      </div>

      {/* Highlights — resolved to "Name: Value" strings by the edge function */}
      {athlete.featured_stats && athlete.featured_stats.length > 0 && (
        <div className="mt-3">
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">
            Highlights
          </p>
          <div className="flex flex-wrap gap-1">
            {athlete.featured_stats.map((s, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-lg text-[9px] font-bold"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Top Stats */}
      {athlete.top_stats && athlete.top_stats.length > 0 && (
        <div className="mt-3">
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
            Top Stats
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {athlete.top_stats.map((s) => (
              <div
                key={s.metric}
                className="bg-slate-50 rounded-xl py-1.5 px-2 flex items-baseline gap-1"
              >
                <span className="text-xs font-black text-slate-900">{s.value}</span>
                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest truncate">
                  {s.metric.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hudl */}
      {athlete.hudl_link && (
        <a
          href={athlete.hudl_link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex items-center justify-center gap-1.5 w-full py-2 rounded-xl bg-orange-500 text-white text-[9px] font-black uppercase tracking-widest hover:bg-orange-600 transition"
        >
          Watch Film on Hudl
        </a>
      )}

      {/* Contact + socials */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {athlete.social_handles?.twitter && (
          <a
            href={`https://x.com/${athlete.social_handles.twitter.replace('@', '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[9px] font-black text-slate-400 hover:text-slate-700 transition uppercase tracking-widest"
          >
            𝕏 {athlete.social_handles.twitter}
          </a>
        )}
        {athlete.social_handles?.instagram && (
          <a
            href={`https://instagram.com/${athlete.social_handles.instagram.replace('@', '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[9px] font-black text-pink-400 hover:text-pink-600 transition uppercase tracking-widest"
          >
            IG {athlete.social_handles.instagram}
          </a>
        )}
      </div>
    </div>
  );
}

function ErrorScreen({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 space-y-4">
      <div className="p-4 bg-slate-50 rounded-full">{icon}</div>
      <h2 className="text-xl font-black uppercase italic text-slate-900">{title}</h2>
      <p className="text-sm font-medium text-slate-500 max-w-xs">{body}</p>
    </div>
  );
}

export const PublicAthleteView: React.FC<PublicAthleteViewProps> = ({ athleteId }) => {
  const [viewState, setViewState] = useState<ViewState>({ kind: 'loading' });

  const [coachName, setCoachName] = useState('');
  const [coachSchool, setCoachSchool] = useState('');
  const [coachEmail, setCoachEmail] = useState('');
  const [contactError, setContactError] = useState('');
  const [contactLoading, setContactLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    void fetchPublicAthlete(athleteId).then((result) => {
      if (result.ok) {
        setViewState({ kind: 'loaded', athlete: result.athlete });
        posthog.capture('scoutpass_viewed', { athlete_id: athleteId });
      } else if (result.code === 'not_found') {
        setViewState({ kind: 'not_found' });
      } else {
        setViewState({ kind: 'error', message: result.message });
      }
    });
  }, [athleteId]);

  const handleContactSubmit = async () => {
    setContactError('');
    const trimmedEmail = coachEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmedEmail)) {
      setContactError('Enter a valid email address.');
      return;
    }
    setContactLoading(true);
    try {
      // TODO: implement notify-athlete-coach-interest edge function
      // Should: (1) insert a row into athlete_coach_contacts table,
      //         (2) optionally send athlete a push/email notification.
      console.warn('notify-athlete-coach-interest not yet deployed — contact captured client-side only');
      posthog.capture('scoutpass_coach_contact_submitted', { athlete_id: athleteId });
      setSubmitted(true);
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-50 z-[400] overflow-y-auto">
      {/* Nav */}
      <div className="sticky top-0 bg-white border-b border-slate-100 z-10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-amber-600 flex items-center justify-center">
            <span className="text-white text-[8px] font-black">SL</span>
          </div>
          <span className="text-xs font-black uppercase italic tracking-widest text-slate-900">
            ScoutLoop
          </span>
        </div>
        <a
          href="https://scoutloop.app"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[9px] font-black text-amber-500 uppercase tracking-widest hover:text-amber-700 transition"
        >
          Get ScoutLoop
        </a>
      </div>

      <div className="max-w-sm mx-auto px-4 pb-12">
        {/* Loading */}
        {viewState.kind === 'loading' && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 size={32} className="animate-spin text-slate-300" />
          </div>
        )}

        {/* Error states */}
        {viewState.kind === 'not_found' && (
          <ErrorScreen
            icon={<AlertTriangle size={32} className="text-slate-400" />}
            title="Profile Not Found"
            body="This ScoutPass link doesn't exist or may have been removed."
          />
        )}
        {viewState.kind === 'error' && (
          <ErrorScreen
            icon={<AlertTriangle size={32} className="text-red-400" />}
            title="Something Went Wrong"
            body={viewState.message || 'Could not load this profile. Please try again.'}
          />
        )}

        {/* Loaded view */}
        {viewState.kind === 'loaded' && (
          <div className="space-y-4 pt-6">
            <AthleteCard athlete={viewState.athlete} />

            {/* Coach contact capture */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">
                Coaching Staff? Leave your info
              </p>
              {!submitted ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={coachName}
                    onChange={(e) => setCoachName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                  <input
                    type="text"
                    placeholder="School / Organization"
                    value={coachSchool}
                    onChange={(e) => setCoachSchool(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                  <input
                    type="email"
                    placeholder="your@school.edu"
                    value={coachEmail}
                    onChange={(e) => {
                      setCoachEmail(e.target.value);
                      setContactError('');
                    }}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                  {contactError && (
                    <p className="text-red-500 text-xs font-bold pl-1">{contactError}</p>
                  )}
                  <button
                    onClick={() => void handleContactSubmit()}
                    disabled={contactLoading || !coachEmail.trim()}
                    className="w-full flex items-center justify-center gap-2 bg-amber-600 text-white py-3.5 rounded-2xl font-black uppercase italic tracking-widest text-xs hover:bg-amber-700 transition disabled:opacity-40"
                  >
                    {contactLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                    {contactLoading ? 'Sending…' : 'Send to Athlete'}
                  </button>
                </div>
              ) : (
                <p className="text-sm font-bold text-emerald-600 text-center py-2">
                  ✓ Sent! The athlete will be in touch.
                </p>
              )}
            </div>

            {/* Footer CTA */}
            <div className="mt-8 text-center space-y-2">
              <p className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">
                Powered by ScoutLoop
              </p>
              <a
                href="https://scoutloop.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-[9px] font-black text-amber-400 hover:text-amber-600 uppercase tracking-widest transition"
              >
                Athletes — build your free ScoutPass →
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
